import { paths } from '@mosey/api-types';
import {
  useRouteLoaderData,
  useSearchParams,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useLegalEntityRegions } from '../../../hooks/useUser';
import { api } from '../../../utils/fetchApi';

export const useDocumentCenterData = () => {
  return useRouteLoaderData('documents') as {
    categories: paths['/api/documents/categories']['get']['responses']['200']['content']['application/json'];
    types: paths['/api/documents/types']['get']['responses']['200']['content']['application/json'];
    documents: paths['/api/documents']['get']['responses']['200']['content']['application/json'];
    workflowShareDocuments: paths['/api/documents/workflow_share_documents']['get']['responses']['200']['content']['application/json'];
  };
};

export const useFilteredDocuments = () => {
  const { documents, types, categories } = useDocumentCenterData();
  const [searchParams] = useSearchParams();
  const regions = useLegalEntityRegions();

  const regionCode = searchParams.get('region')?.toLowerCase();
  const categoryId = searchParams.get('category');

  return useMemo(() => {
    const selectedRegion = regionCode
      ? regions.find((r) => r.region.id.toLowerCase() === regionCode)?.region
      : undefined;

    const filteredTypes = types.filter((type) => {
      const matchesCategory = categoryId
        ? type.document_category_id?.toString() === categoryId
        : true;

      return matchesCategory;
    });

    const filteredDocuments = regionCode
      ? documents.filter((doc) => doc.region_code?.toLowerCase() === regionCode)
      : documents;

    const usedCategoryIds = new Set(
      filteredTypes.map((t) => t.document_category_id),
    );
    const filteredCategories = categories.filter((cat) =>
      usedCategoryIds.has(cat.id),
    );

    return {
      documents: filteredDocuments,
      types: filteredTypes,
      categories: filteredCategories,
      selectedRegion,
    };
  }, [documents, types, categories, regions, regionCode, categoryId]);
};

export const useSignedDocumentUrl = () => {
  const { documentId: documentId } = useParams();
  const { workflowShareDocuments, documents } = useDocumentCenterData();

  const navigate = useNavigate();
  const location = useLocation();
  const [url, setUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready'>('idle');

  const directMatch = documents.find((d) => d.id === documentId);

  const sharedMatch = workflowShareDocuments.find(
    (share) => share.workflow_share_id === documentId,
  );

  useEffect(() => {
    if (!documentId || directMatch) return;

    if (sharedMatch) {
      navigate(`/documents/${sharedMatch.document_id}${location.search}`, {
        replace: true,
      });
    }
  }, [documentId, directMatch, sharedMatch, navigate, location.search]);

  const matchedDocument = directMatch;

  useEffect(() => {
    if (!matchedDocument) {
      setUrl(null);
      setStatus('idle');
      return;
    }

    const fetchUrl = async () => {
      setStatus('loading');
      const res = await api({
        url: `/api/documents/signed/${matchedDocument.id}`,
        method: 'GET',
      });
      const { url } = await res.json();
      setUrl(url);
      setStatus('ready');
    };

    fetchUrl();
  }, [matchedDocument]);

  return { url, status, matchedDocument };
};

export const useDocumentsByCategory = () => {
  const { documents, types, categories, selectedRegion } =
    useFilteredDocuments();

  const typeMap = useMemo(
    () => Object.fromEntries(types.map((t) => [t.id, t])),
    [types],
  );

  const documentsByCategory = useMemo(() => {
    return categories.reduce<Record<number, typeof documents>>((acc, cat) => {
      acc[cat.id] = documents.filter((doc) => {
        const type = typeMap[doc.document_type_id];
        return type?.document_category_id === cat.id;
      });
      return acc;
    }, {});
  }, [documents, categories, typeMap]);

  return { categories, documentsByCategory, typeMap, selectedRegion };
};
